import clsx from 'clsx';
import { CheckCircle2, Circle, Info, Loader2, MinusCircle, RotateCcw, TriangleAlert, XCircle } from '@/components/icons';
import type { FailureInfo, Platform, StepResult, StepStatus } from '@1qa/shared';
import { describeStep } from '@/features/builder/describe';
import { splitPlaceholders } from '@/lib/testData';
import { formatDuration } from '@/lib/format';
import { FailureReasonBadge } from './FailureReason';

const STATUS: Record<StepStatus, { label: string; Icon: typeof Circle; className: string; spin?: boolean }> = {
  PENDING: { label: 'Not started yet', Icon: Circle, className: 'text-fg-muted' },
  RUNNING: { label: 'Running', Icon: Loader2, className: 'text-primary', spin: true },
  PASSED: { label: 'Passed', Icon: CheckCircle2, className: 'text-success' },
  FAILED: { label: 'Failed', Icon: XCircle, className: 'text-danger-text' },
  ERROR: { label: 'Error', Icon: TriangleAlert, className: 'text-warning' },
  SKIPPED: { label: 'Not run', Icon: MinusCircle, className: 'text-fg-muted' },
};

/**
 * Why steps were not run, shown once on the first step of each stretch of skipped steps: the step that failed
 * before them, or that the test ended (or never started) before reaching them. A step's own log wins (security checks).
 */
export function skipReasons(steps: StepResult[]): (string | undefined)[] {
  return steps.map((s, i) => {
    if (s.status !== 'SKIPPED') return undefined;
    if (s.log) return s.log;
    if (i > 0 && steps[i - 1]!.status === 'SKIPPED' && !steps[i - 1]!.log) return undefined;
    let end = i;
    while (end + 1 < steps.length && steps[end + 1]!.status === 'SKIPPED' && !steps[end + 1]!.log) end++;
    const which = end > i ? `Steps ${i + 1}–${end + 1} were` : 'This step was';
    let failed = -1;
    for (let j = i - 1; j >= 0; j--) {
      if (steps[j]!.status === 'FAILED' || steps[j]!.status === 'ERROR') {
        failed = j;
        break;
      }
    }
    if (failed >= 0) {
      const what = steps[failed]!.status === 'ERROR' ? 'stopped with an error' : 'failed';
      return `${which} not run: step ${failed + 1} ${what}, so the test stopped there.`;
    }
    if (i === 0) return `${which} not run: the test did not start (see the reason below).`;
    return `${which} not run: the test ended before reaching ${end > i ? 'them' : 'it'} (see the error details below).`;
  });
}

/** The readable preview of a test (as in the builder), with each step's status; `failure` marks the step that failed and why. */
export function StepList({ steps, platform, failure }: { steps: StepResult[]; platform?: Platform; failure?: FailureInfo | null }) {
  const reasons = skipReasons(steps);
  return (
    <ol className="space-y-2 text-sm">
      {steps.map((s, i) => {
        const skipped = reasons[i];
        const m = STATUS[s.status] ?? STATUS.PENDING;
        const muted = s.status === 'PENDING' || s.status === 'SKIPPED';
        return (
          <li
            key={i}
            className={clsx(
              'flex items-start gap-3 rounded-lg px-2 py-1.5',
              s.status === 'RUNNING' && 'bg-primary-subtle/50',
              s.status === 'FAILED' && 'bg-danger-subtle',
              s.status === 'ERROR' && 'bg-warning-subtle',
            )}
          >
            <m.Icon className={clsx('mt-0.5 size-4 shrink-0', m.className, m.spin && 'motion-safe:animate-spin')} aria-hidden />
            <span className="sr-only">{m.label}:</span>
            <span className="w-5 shrink-0 text-right font-display text-xs text-fg-muted tabular-nums">{i + 1}</span>
            <div className="min-w-0 flex-1">
              <span className={clsx(muted ? 'text-fg-muted' : 'text-fg')}>
                {splitPlaceholders(describeStep({ id: String(i), kind: s.kind, value: s.value }, platform)).map((seg, j) =>
                  seg.placeholder ? (
                    <mark key={j} className="rounded-bar bg-primary-subtle px-1 font-mono text-[12px] text-primary">
                      {seg.text}
                    </mark>
                  ) : (
                    <span key={j}>{seg.text}</span>
                  ),
                )}
              </span>
              {s.data?.map((d, j) => (
                <p key={j} className="mt-1 flex items-start gap-1.5 text-xs text-fg-secondary">
                  <Info className={clsx('mt-px size-3.5 shrink-0', d.source === 'written' ? 'text-warning' : 'text-link')} aria-hidden />
                  <span>
                    <span className="font-medium">
                      {d.source === 'written' ? `Used the value written in the test for ${d.name}: “${d.value ?? ''}”.` : `Used the shared value for ${d.name}.`}
                    </span>{' '}
                    {d.reason}
                  </span>
                </p>
              ))}
              {skipped && (
                <p className="mt-1 flex items-start gap-1.5 text-xs text-fg-secondary">
                  <MinusCircle className="mt-px size-3.5 shrink-0 text-fg-muted" aria-hidden />
                  <span>{skipped}</span>
                </p>
              )}
            </div>
            {failure?.stepIndex === i && <FailureReasonBadge failure={failure} />}
            {!!s.retries && (
              <span
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-warning-subtle px-2.5 py-0.5 text-xs font-medium text-warning ring-1 ring-warning/40 ring-inset"
                title={s.status === 'PASSED' ? 'This step failed at first and passed when tried again. It may be flaky.' : 'This step was tried again before it failed.'}
              >
                <RotateCcw className="size-3" aria-hidden />
                {s.status === 'PASSED' ? 'Passed on retry' : 'Retried'}
                {s.retries > 1 ? ` ×${s.retries}` : ''}
              </span>
            )}
            <span className="shrink-0 text-xs text-fg-muted tabular-nums">
              {s.status === 'RUNNING' ? 'Running…' : s.durationMs !== undefined ? formatDuration(s.durationMs) : ''}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
